import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { PACK_ART, StitchDetail } from "./PackArt";

export function usePacks() {
  const [packs, setPacks] = useState(null);
  useEffect(() => { api.get("/public/packs").then((r) => setPacks(r.data)).catch(() => setPacks([])); }, []);
  return packs;
}

export const fmtEur = (n) => `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

export function PackPhoto({ src, alt, illustration, art, credit, className = "", position = "center" }) {
  const Art = art && PACK_ART[art];
  if (Art || !src) {
    const Comp = Art || StitchDetail;
    return <figure className={`relative overflow-hidden bg-[#F4F0EA] ${className}`} data-testid="pack-art"><Comp className="absolute inset-0 w-full h-full" /></figure>;
  }
  return (
    <figure className={`relative overflow-hidden bg-[#EDE7DD] ${className}`}>
      <img src={src} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: position }} />
      {illustration && <figcaption className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.16em] bg-[#FBF9F5]/90 text-[#57534E] px-2 py-1" data-testid="pack-photo-illustration">Illustration — réalisation de l'atelier</figcaption>}
      {credit && !illustration && <figcaption className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.16em] bg-[#FBF9F5]/90 text-[#57534E] px-2 py-1" data-testid="pack-photo-credit">{credit}</figcaption>}
    </figure>
  );
}

export function PackSection() {
  const { t } = useT();
  const packs = usePacks();
  const p = packs?.[0];
  if (!p) return null;
  const unit = p.price_ht / p.quantity;
  return (
    <section className="w-full bg-[#1C1917] text-[#FBF9F5]" aria-labelledby="pack-title" data-testid="home-pack">
      <div className="grid lg:grid-cols-12 lg:min-h-[78vh]">
        <div className="lg:col-span-6 grid grid-cols-3 lg:grid-cols-2 lg:grid-rows-[1fr_auto] gap-px bg-[#FBF9F5]/10">
          {p.variants.map((v) => <PackPhoto key={v.key} src={v.photo} art={v.art} alt={`${v.label} — matière`} illustration={v.photo_is_illustration} credit={v.label} className="aspect-[5/6] lg:aspect-auto lg:min-h-[320px]" position="center 40%" />)}
          <PackPhoto src={p.detail_photo} alt="Broderie réalisée à l'atelier sur un sweat" illustration={p.detail_photo_is_illustration} credit={p.detail_photo_credit} className="aspect-[5/6] lg:aspect-[16/7] lg:col-span-2" position="center 45%" />
        </div>
        <div className="lg:col-span-6 px-5 sm:px-8 lg:px-16 py-12 lg:py-20 flex flex-col justify-center">
          <p className="k-eyebrow text-[#C25E38]">{t("Offre de saison")} — {p.name}</p>
          <h2 id="pack-title" className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]" data-testid="pack-headline">{p.headline}</h2>
          <p className="text-base md:text-lg mt-4 opacity-85 max-w-xl">{p.tagline}</p>
          <p className="mt-6 text-sm opacity-75 max-w-xl leading-relaxed">
            {p.quantity} {p.variants.map((v) => v.plural).join(` ${t("ou")} ${p.quantity} `)}, {t("avec le même logo brodé au cœur. Tailles assorties, digitalisation standard du logo et BAT inclus.")}
          </p>
          <p className="mt-6 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-4xl" data-testid="pack-price">{fmtEur(p.price_ht)} <span className="text-base font-sans opacity-75">HT {t("le pack")}</span></span>
            <span className="text-sm opacity-75">— {t("soit")} {fmtEur(unit)} HT {t("par pièce")}</span>
          </p>
          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <Link to={`/demande/vetements?pack=${p.id}`} className="k-btn bg-[#C25E38] border-[#C25E38] hover:bg-[#FBF9F5] hover:text-[#1C1917] hover:border-[#FBF9F5]" data-testid="pack-cta">{p.cta} <ArrowRight size={16} /></Link>
            <Link to={`/pack/${p.id}`} className="k-link text-sm text-[#FBF9F5]" data-testid="pack-details-link">{t("Conditions et détails du pack")}</Link>
          </div>
          <p className="mt-6 text-xs opacity-60 max-w-xl">{t("Logo jusqu'à")} {p.logo_limits.max_cm} × {p.logo_limits.max_cm} cm, {p.logo_limits.max_colors} {t("couleurs de fil")}, {p.logo_limits.max_stitches.toLocaleString("fr-FR")} {t("points maximum")}. {t("Marquage dos en supplément sur devis. Demande de devis, sans commande automatique.")}{p.photo_credit && <span className="block mt-1 opacity-70">{p.photo_credit}</span>}</p>
        </div>
      </div>
    </section>
  );
}
